import { useState } from 'react';
import {
  Button,
  InlineStack,
  Paragraph,
  Popover,
  PopoverContent,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { isFlexPlan } from '@/services/auth/plan-slugs';
import { useOpenActivityHistoryAddon } from '../hooks/use-open-activity-history-addon';
import { catalogPriceCents } from '../hooks/use-addon-price';
import { useAddons } from '../hooks/use-addons';
import { activityHistory, type ActivityHistoryKind, gatedIncludedDays } from '../utils/activity-history';
import { formatAddonPriceCents, formatShortDateWithYear, perBillingCycle } from '../utils/addon-purchase';
import * as styles from './activity-history-chip.css';
import clsx from 'clsx';
import { FlexMark, leadingMarkButton } from '@/components/flex-mark';
import * as addonStyles from './segments-addon-button.css';

type ActivityHistoryChipProps = {
  kind: ActivityHistoryKind;
};

/** How far back a Flex account can see message or profile activity, and the add-on that extends it. */
export function ActivityHistoryChip({ kind }: ActivityHistoryChipProps) {
  const { account } = useAuth();
  const { data: addons } = useAddons();
  // Until they load, or when they fail to, a retention holder would see the upsell.
  if (!isFlexPlan(account) || addons == null) return null;
  if (addons.purchased.some((addon) => addon.product === 'data_retention')) return null;

  const priceCents = catalogPriceCents(addons, 'data_retention');
  return <FlexActivityHistoryChip kind={kind} price={priceCents == null ? null : formatAddonPriceCents(priceCents)} />;
}

type FlexActivityHistoryChipProps = ActivityHistoryChipProps & { price: string | null };

function FlexActivityHistoryChip({ kind, price }: FlexActivityHistoryChipProps) {
  const priceLine = perBillingCycle(price);
  const [isOpen, setIsOpen] = useState(false);
  const { account } = useAuth();
  const history = activityHistory(kind, new Date(), gatedIncludedDays(account, kind));
  const openAddon = useOpenActivityHistoryAddon();

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} withArrow={false} placement="bottom-end">
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="tertiary"
          isActive={isOpen}
          elementBefore={<FlexMark />}
          data-test-activity-history-chip={kind}
        >
          <Text text="product-p" color="inherit">
            Data retention
          </Text>
        </Button>
      )}

      <PopoverContent className={styles.popoverContent}>
        <Stack gap="150">
          <Text text="product-p-active">{history.heading}</Text>
          <Stack gap="075">
            <InlineStack
              className={`${styles.row} ${styles.includedRow}`}
              gap="150"
              alignBlock="center"
              alignInline="space-between"
            >
              <Text text="product-psm">Included</Text>
              <Text text="product-psm-active">Back to {formatShortDateWithYear(history.lookbackStart)}</Text>
            </InlineStack>
            <InlineStack className={styles.row} gap="150" alignBlock="center" alignInline="space-between">
              <Stack>
                <Paragraph text="product-psm">
                  With data retention
                  {priceLine == null ? null : (
                    <>
                      {' '}
                      <Text as="span" text="product-pxs-secondary">
                        {priceLine}
                      </Text>
                    </>
                  )}
                </Paragraph>
                <Paragraph text="product-psm-active">
                  Back to {formatShortDateWithYear(history.addonLookbackStart)}
                </Paragraph>
              </Stack>
              <Button
                className={clsx(addonStyles.flexTag, leadingMarkButton)}
                size="sm"
                elementBefore={<FlexMark />}
                onClick={() => {
                  setIsOpen(false);
                  void openAddon();
                }}
              >
                Flex add-on
              </Button>
            </InlineStack>
          </Stack>
          <Paragraph text="product-pxs-secondary">
            Nothing is deleted. Add it and hidden data reappears. It also extends metrics, journeys, condition lookback
            and the audit log.
          </Paragraph>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}
